/**
 * Offline audio store on top of the browser Cache API.
 *
 * TL;DR — the app itself is a PWA whose HTML/CSS/JS are cached by the Service
 * Worker on first visit, so `slowson.com` opens even without network. On top
 * of that, users can explicitly mark tracks for offline listening: we fetch
 * the full MP3, drop it in a named Cache, and the audio element gets the
 * cache blob instead of the network on subsequent plays.
 *
 * Storage budget:
 *   - iOS Safari : ~50 MB per site  (harsh — that's ~15 MP3 128k tracks)
 *   - Chrome/Android : 60% of disk free space (essentially unlimited)
 *   - Desktop Chrome : same
 *
 * Design:
 *   - One Cache instance named `sonslow-tracks-v1` (bumped only on breaking
 *     changes to the serialization; the cache tier is orthogonal to code
 *     updates and shouldn't nuke on every deploy).
 *   - Key = the /api/stream/{id} URL, matching how the player would request
 *     it — so `caches.match(fetchRequest)` finds it transparently.
 *   - We keep a lightweight index in localStorage (`sl.offline.ids`) so the UI
 *     can show which tracks are saved without opening the Cache each time.
 */

const CACHE_NAME = "sonslow-tracks-v1";
const INDEX_KEY = "sl.offline.ids";

function loadIndex(): Set<string> {
  try {
    const raw = localStorage.getItem(INDEX_KEY);
    return new Set(raw ? (JSON.parse(raw) as string[]) : []);
  } catch { return new Set(); }
}
function saveIndex(s: Set<string>) {
  try { localStorage.setItem(INDEX_KEY, JSON.stringify([...s])); } catch {}
}

let mem = loadIndex();

export function isOffline(trackId: string): boolean {
  return mem.has(trackId);
}

export function listOffline(): string[] {
  return [...mem];
}

function urlFor(trackId: string, token: string | null): string {
  const q = token ? `?token=${encodeURIComponent(token)}` : "";
  return `/api/stream/${trackId}${q}`;
}

export async function saveTrack(trackId: string, token: string | null,
                                onProgress?: (bytes: number, total: number) => void): Promise<boolean> {
  if (!("caches" in window)) return false;
  const cache = await caches.open(CACHE_NAME);
  const url = urlFor(trackId, token);
  try {
    const res = await fetch(url, { credentials: "same-origin" });
    if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);
    // Read the stream to report progress, then reconstruct a Response we can
    // cache. We swallow Content-Length errors gracefully.
    const total = Number(res.headers.get("content-length") ?? 0);
    const reader = res.body.getReader();
    const chunks: Uint8Array[] = [];
    let received = 0;
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      if (value) { chunks.push(value); received += value.byteLength; onProgress?.(received, total); }
    }
    const blob = new Blob(chunks as BlobPart[], { type: res.headers.get("content-type") ?? "audio/mpeg" });
    // Cache the *URL without the token* so re-issued JWTs still hit the cache.
    const cacheUrl = urlFor(trackId, null);
    const cachedResponse = new Response(blob, {
      headers: {
        "Content-Type": res.headers.get("content-type") ?? "audio/mpeg",
        "Content-Length": String(blob.size),
        "X-Sonslow-Source": "offline",
      },
    });
    await cache.put(cacheUrl, cachedResponse);
    mem.add(trackId); saveIndex(mem);
    return true;
  } catch (err) {
    console.warn("offline save failed", err);
    return false;
  }
}

export async function removeTrack(trackId: string): Promise<void> {
  if (!("caches" in window)) return;
  const cache = await caches.open(CACHE_NAME);
  await cache.delete(urlFor(trackId, null));
  mem.delete(trackId); saveIndex(mem);
}

/** Look up an offline blob URL (or null if not saved). Used by the player. */
export async function offlineUrl(trackId: string): Promise<string | null> {
  if (!("caches" in window)) return null;
  if (!mem.has(trackId)) return null;
  try {
    const cache = await caches.open(CACHE_NAME);
    const res = await cache.match(urlFor(trackId, null));
    if (!res) return null;
    const blob = await res.blob();
    return URL.createObjectURL(blob);
  } catch { return null; }
}

export async function estimateStorage(): Promise<{ usage: number; quota: number } | null> {
  if (typeof navigator === "undefined" || !navigator.storage?.estimate) return null;
  const e = await navigator.storage.estimate();
  return { usage: e.usage ?? 0, quota: e.quota ?? 0 };
}
